import { AppPathValue, paths } from '../../constants/paths';
import { ProfileStatus } from '../../enum/enum';

/**
 * Matriz central de rotas e suas regras de acesso
 *
 * Cada rota define:
 * - path: valor do paths
 * - public: true se não precisa de login
 * - publicReason: motivo da rota pública
 * - restrictions: regras para rotas privadas
 * - redirectFallback: rota de fallback se não cumprir restrições
 */

/**
 * Tipo de restrições que uma rota pode ter
 */
export type RouteRestriction =
  | 'requiresAuth' // Requer autenticação
  | 'requiresApprovedProfile' // Requer perfil aprovado
  | 'requiresCompletedOnboarding' // Requer onboarding completo
  | 'requiresActiveSubscription' // Requer assinatura ativa
  | 'allowInactiveAccount' // Permite acesso com conta inativa
  | 'requiresParams'
  | 'requiresDiversityOnboarding';

/**
 * Configuração de cada rota
 */
export interface RouteConfig {
  path: AppPathValue;
  public?: boolean;
  publicReason?: string;
  restrictions?: RouteRestriction[];
  redirectFallback?: AppPathValue;
}

const ROUTES: RouteConfig[] = [
  // === Para desenvolvimento (acessível só localmente) ===
  { path: paths.dev, public: true, publicReason: 'Desenvolvimento' },
  // === Gerais ===
  { path: paths.home, public: true, publicReason: 'Institucional' },
  { path: paths.notFound, public: true, publicReason: '404 Página não encontrada' },
  {
    path: paths.internalServerError,
    public: true,
    publicReason: '500 Instabilidade no servidor',
  },

  // === Perfil ===
  {
    path: paths.perfil.privacidade.conta.apagar._,
    public: false,
    restrictions: ['requiresAuth'],
  },
  {
    path: paths.perfil.privacidade.conta.apagar.confirmar,
    public: false,
    restrictions: ['requiresAuth'],
  },
  {
    path: paths.perfil.privacidade.conta.desativar._,
    public: false,
    restrictions: ['requiresAuth'],
  },
  {
    path: paths.perfil.privacidade.conta.desativar.confirmar,
    public: false,
    restrictions: ['requiresAuth'],
  },
  {
    path: paths.perfil.privacidade.conta.reativar._,
    public: false,
    restrictions: ['requiresAuth', 'allowInactiveAccount'],
  },
  {
    path: paths.perfil.privacidade.conta.reativar.confirmar,
    public: false,
    restrictions: ['requiresAuth', 'allowInactiveAccount'],
  },
  {
    path: paths.perfil.seguranca.minhaConta,
    public: false,
    restrictions: ['requiresAuth'],
  },
  {
    path: paths.perfil.seguranca.redefinirSenhaConfirmacao,
    public: false,
    restrictions: ['requiresAuth'],
  },

  // === Saúde ===
  {
    path: paths.saude.profissional.agenda._,
    public: false,
    restrictions: ['requiresAuth', 'requiresCompletedOnboarding'],
  },
  {
    path: paths.saude.profissional.agenda.detalhes._,
    public: false,
    restrictions: ['requiresAuth', 'requiresCompletedOnboarding'],
  },
  {
    path: paths.saude.profissional.agenda.detalhes.confirmacao,
    public: false,
    restrictions: ['requiresAuth', 'requiresCompletedOnboarding'],
  },
  {
    path: paths.saude.profissional.agendaSync,
    public: false,
    restrictions: [
      'requiresAuth',
      'requiresCompletedOnboarding',
      'requiresActiveSubscription',
    ],
  },
  {
    path: paths.saude.profissional.cadastrado,
    public: false,
    restrictions: [
      'requiresAuth',
      'requiresCompletedOnboarding',
      'requiresActiveSubscription',
    ],
  },
  {
    path: paths.saude.profissional.cadastrar._,
    public: false,
    restrictions: [
      'requiresAuth',
      'requiresCompletedOnboarding',
      'requiresActiveSubscription',
    ],
  },
  {
    path: paths.saude.profissional.cadastrar.atendimento._,
    public: false,
    restrictions: [
      'requiresAuth',
      'requiresCompletedOnboarding',
      'requiresActiveSubscription',
    ],
  },
  {
    path: paths.saude.profissional.cadastrar.atendimento.confirmacao,
    public: false,
    restrictions: [
      'requiresAuth',
      'requiresCompletedOnboarding',
      'requiresActiveSubscription',
    ],
  },
  {
    path: paths.saude.profissional.cadastrar.diversidade._,
    public: false,
    restrictions: [
      'requiresAuth',
      'requiresCompletedOnboarding',
      'requiresActiveSubscription',
    ],
  },
  {
    path: paths.saude.profissional.cadastrar.diversidade.resumo,
    public: false,
    restrictions: [
      'requiresAuth',
      'requiresCompletedOnboarding',
      'requiresActiveSubscription',
    ],
  },
  {
    path: paths.saude.profissional.cadastrar.diversidade.recortesSociais,
    public: false,
    restrictions: [
      'requiresAuth',
      'requiresCompletedOnboarding',
      'requiresActiveSubscription',
    ],
  },
  {
    path: paths.saude.profissional.painelInicial,
    public: false,
    restrictions: ['requiresAuth', 'requiresApprovedProfile'],
    redirectFallback: paths.saude.profissional.preCadastro._,
  },
  {
    path: paths.saude.profissional.preCadastro._,
    public: true,
    publicReason: 'Página de pré-cadastro',
  },
  {
    path: paths.saude.profissional.preCadastro.atendimento._,
    public: true,
    publicReason: 'Página de pré-cadastro - atendimento',
  },
  {
    path: paths.saude.profissional.preCadastro.atendimento.agenda,
    public: true,
    publicReason: 'Página de pré-cadastro - atendimento agenda',
  },
  {
    path: paths.saude.profissional.preCadastro.confirmacao,
    public: true,
    publicReason: 'Página de pré-cadastro - confirmação',
  },
  {
    path: paths.saude.profissional.preCadastro.verificando,
    public: true,
    publicReason: 'Página de pré-cadastro - verificação de inscrição',
  },

  // === Redefinição de senha ===
  {
    path: paths.saude.profissional.redefinirSenha._,
    public: true,
    publicReason: 'Redefinir senha',
  },
  {
    path: paths.saude.profissional.redefinirSenha.verificar,
    public: true,
    publicReason: 'Redefinir senha - verificar e-mail',
  },
  {
    path: paths.saude.profissional.redefinirSenha.confirmar,
    public: true,
    publicReason: 'Redefinir senha - cadastrar nova senha',
    restrictions: ['requiresParams'],
  },
  {
    path: paths.saude.profissional.redefinirSenha.linkExpirado,
    public: true,
    publicReason: 'Redefinir senha - link expirado',
  },
  {
    path: paths.saude.profissional.redefinirSenha.alterada,
    public: true,
    publicReason: 'Redefinir senha - senha alterada com sucesso',
  },
];

// === Funções de verificação de rota ===
export const isPublicRoute = () => {
  const path = window.location.pathname as AppPathValue;
  const route = ROUTES.find((r) => r.path === path);
  return route?.public ?? false;
};

export const getRouteAccessRules = (path: AppPathValue) => {
  const route = ROUTES.find((r) => r.path === path);
  return route?.restrictions ?? [];
};

// === Função de redirecionamento baseada no status do usuário ===

export const redirectUserBasedOnApprovalStatus = (
  status?: ProfileStatus,
  postRegistrationUnderAnalysis = false,
  published = false,
  hasProfessionalProfile = true,
): AppPathValue | undefined => {
  if (!hasProfessionalProfile) return paths.saude.profissional.preCadastro._;
  if (!status) return paths.saude.profissional.preCadastro._;
  const STATUS_REDIRECTS: Record<ProfileStatus, AppPathValue> = {
    pending: paths.saude.profissional.preCadastro._,
    in_review: paths.saude.profissional.preCadastro.verificando,
    approved: paths.saude.profissional.painelInicial,
    rejected: paths.saude.profissional.preCadastro._,
    deleted: paths.perfil.seguranca.minhaConta,
  };

  if (postRegistrationUnderAnalysis)
    return paths.saude.profissional.preCadastro.verificando;
  if (status === ProfileStatus.APPROVED && published)
    return paths.saude.profissional.painelInicial;

  return STATUS_REDIRECTS[status];
};
